// 主框架 + 路由
import React, { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { ToastProvider, useToast } from './ui.jsx';
import { api, auth } from './api.js';

import Login from './pages/Login.jsx';
import Dashboard from './pages/Dashboard.jsx';
import Customers from './pages/Customers.jsx';
import Suppliers from './pages/Suppliers.jsx';
import Items from './pages/Items.jsx';
import Quotations from './pages/Quotations.jsx';
import QuotationEditor from './pages/QuotationEditor.jsx';
import Orders from './pages/Orders.jsx';
import OrderDetail from './pages/OrderDetail.jsx';
import Invoices from './pages/Invoices.jsx';
import AR from './pages/AR.jsx';
import PurchaseOrders from './pages/PurchaseOrders.jsx';
import SupplierInvoices from './pages/SupplierInvoices.jsx';
import Reports from './pages/Reports.jsx';
import Settings from './pages/Settings.jsx';

const NAVS = [
  { to: '/dashboard', t: '儀表板' },
  { to: '/quotations', t: '報價單' },
  { to: '/orders', t: '工程訂單' },
  { to: '/invoices', t: '發票' },
  { to: '/ar', t: '應收帳款' },
  { to: '/pos', t: '分判商' },
  { to: '/supplier-invoices', t: '供應商發票' },
  { to: '/customers', t: '客戶' },
  { to: '/suppliers', t: '供應商' },
  { to: '/items', t: '項目庫' },
  { to: '/reports', t: '報表' },
  { to: '/settings', t: '設定' },
];

const TITLES = [
  [/^\/quotations\/new/, ['📄', '新增報價單']],
  [/^\/quotations/, ['📄', '報價單 Quotations']],
  [/^\/orders/, ['🏗️', '工程訂單 Orders']],
  [/^\/invoices/, ['🧾', '發票 Invoices']],
  [/^\/ar/, ['💰', '應收帳款中心 A/R']],
  [/^\/pos/, ['📦', '分判商 Sub-contractors']],
  [/^\/supplier-invoices/, ['🚚', '供應商發票']],
  [/^\/customers/, ['🏢', '客戶 Customers']],
  [/^\/suppliers/, ['🏭', '供應商 Suppliers']],
  [/^\/items/, ['🧰', '項目庫 Items']],
  [/^\/reports/, ['📈', '報表中心 Reports']],
  [/^\/settings/, ['⚙️', '系統設定 Settings']],
];

function TopBar() {
  const nav = useNavigate();
  const { toast } = useToast();
  const user = auth.user;
  const [path, setPath] = useState(window.location.pathname);
  const [q, setQ] = useState('');
  const [overdue, setOverdue] = useState(0);
  useEffect(() => { navListen(setPath); }, []);
  useEffect(() => {
    api.get('/dashboard').then(d => setOverdue(d.overdue > 0 ? 1 : 0)).catch(() => {});
  }, [path]);
  const hit = TITLES.find(([re]) => re.test(path));
  const title = hit ? hit[1] : ['📊', '儀表板 Overview'];
  const kw = q.trim().toLowerCase();
  const hits = kw ? NAVS.filter(n => n.t.toLowerCase().includes(kw) || n.to.includes(kw)).slice(0, 6) : [];
  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) {}
    auth.clear();
    toast('已登出，下次見 👋');
    nav('/login');
  };
  return (
    <header className="topbar">
      <div className="topbar-title"><span>{title[0]}</span>{title[1]}</div>
      <div className="topbar-searchwrap">
        <input className="input" placeholder="🔍 快速跳轉 — 輸入頁面名…" value={q} onChange={e => setQ(e.target.value)} />
        {kw && hits.length > 0 ? (
          <div className="jump-menu">
            {hits.map(h => <button key={h.to} onClick={() => { nav(h.to); setQ(''); }}>→ {h.t}</button>)}
          </div>
        ) : null}
      </div>
      <div style={{ flex: 1 }} />
      <button className="icon-btn" title={overdue ? '有逾期款項 — 撳去追數中心' : '通知'} onClick={() => nav('/ar')}>
        🔔{overdue ? <span className="dot" /> : null}
      </button>
      {user ? (
        <>
          <button className="avatar-btn" title="設定" onClick={() => nav('/settings')}>
            <span className="avatar-circle">{(user.display_name || user.username || '?').slice(0, 1)}</span>
            <span className="avatar-name">{user.display_name || user.username}</span>
          </button>
          <button className="icon-btn" title="登出" onClick={logout}>🚪</button>
        </>
      ) : null}
    </header>
  );
}

function Sidebar() {
  const [overdue, setOverdue] = useState(0);
  const { toast } = useToast();
  const nav = useNavigate();
  const user = auth.user;
  useEffect(() => {
    api.get('/dashboard').then(d => setOverdue(d.overdue > 0 ? 1 : 0)).catch(() => {});
  }, []);
  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (e) {}
    auth.clear();
    toast('已登出，下次見 👋');
    nav('/login');
  };
  const N = ({ to, ico, children }) => (
    <NavLink to={to} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
      <span className="nav-ico">{ico}</span><span className="nav-label">{children}</span>
    </NavLink>
  );
  return (
    <aside className="sidebar">
      <NavLink to="/dashboard" className="brand" style={{ textDecoration: 'none' }}>
        <div className="brand-icon">🏗️</div>
        <div className="brand-text">
          <div className="brand-name">Skycrafter 天神村</div>
          <div className="brand-sub">工程業務管理系統</div>
        </div>
      </NavLink>
      <div className="nav-sec">日常營運</div>
      <N to="/dashboard" ico="📊">儀表板</N>
      <N to="/quotations" ico="📄">報價單</N>
      <N to="/orders" ico="🏗️">工程訂單</N>
      <N to="/invoices" ico="🧾">發票</N>
      <N to="/ar" ico="💰">應收帳款{overdue ? <span className="nav-badge">!</span> : null}</N>
      <div className="nav-sec">分判及成本</div>
      <N to="/pos" ico="📦">分判商</N>
      <N to="/supplier-invoices" ico="🚚">供應商發票</N>
      <div className="nav-sec">資料庫</div>
      <N to="/customers" ico="🏢">客戶</N>
      <N to="/suppliers" ico="🏭">供應商</N>
      <N to="/items" ico="🧰">項目庫</N>
      <div className="nav-sec">分析</div>
      <N to="/reports" ico="📈">報表</N>
      <N to="/settings" ico="⚙️">設定</N>
      {user ? (
        <div style={{ marginTop: 'auto', paddingTop: 14 }}>
          <div className="side-user row" style={{ gap: 10, padding: '10px 12px' }}>
            <span className="avatar v3" style={{ width: 34, height: 34, fontSize: 13 }}>
              {(user.display_name || user.username || '?').slice(0, 1)}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="su-name" style={{ fontWeight: 800, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.display_name || user.username}
              </div>
              <div className="su-role" style={{ fontSize: 10.5, fontWeight: 700 }}>
                {user.role === 'admin' ? '👑 管理員' : '使用者'}
              </div>
            </div>
            <button className="btn sm" style={{ padding: '5px 9px' }} onClick={logout} title="登出">🚪</button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}

function RequireAuth({ children }) {
  if (!auth.token) return <Navigate to="/login" replace />;
  return children;
}

function HomeFab() {
  const nav = useNavigate();
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const un = navListen(setPath);
    return un;
  }, []);
  if (path === '/dashboard' || path === '/') return null;
  return (
    <button className="home-fab" title="返主頁" onClick={() => nav('/dashboard')}>🏠</button>
  );
}
function navListen(set) {
  const h = () => set(window.location.pathname);
  window.addEventListener('popstate', h);
  const orig = window.history.pushState;
  window.history.pushState = function (...args) { orig.apply(this, args); h(); };
  return () => { window.removeEventListener('popstate', h); window.history.pushState = orig; };
}

export default function App() {
  return (
    <ToastProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/account" element={<Navigate to="/login" replace />} />
        <Route path="*" element={
          <RequireAuth>
            <div className="app">
              <Sidebar />
              <div className="main-col">
                <TopBar />
                <main className="main">
                  <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/customers" element={<Customers />} />
                  <Route path="/suppliers" element={<Suppliers />} />
                  <Route path="/items" element={<Items />} />
                  <Route path="/quotations" element={<Quotations />} />
                  <Route path="/quotations/new" element={<QuotationEditor />} />
                  <Route path="/quotations/:id" element={<QuotationEditor />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/orders/:id" element={<OrderDetail />} />
                  <Route path="/invoices" element={<Invoices />} />
                  <Route path="/ar" element={<AR />} />
                  <Route path="/pos" element={<PurchaseOrders />} />
                  <Route path="/supplier-invoices" element={<SupplierInvoices />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/settings" element={<Settings />} />
                </Routes>
              </main>
            </div>
            </div>
            <HomeFab />
          </RequireAuth>
        } />
      </Routes>
    </ToastProvider>
  );
}
